import { DictType, Locale } from "@pma/types";

import { parseCookies } from "nookies";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LOCALE } from "@pma/utils/constants";
import { getDictionary } from "@pma/locale/dictionaries";

// Shared cache so every hook instance gets the dictionary immediately
const dictionaryCache: Record<string, DictType> = {};

// Pre-warm both locales so translate() never falls back to English on first render
getDictionary("he").then((d) => { dictionaryCache["he"] = d; });
getDictionary("en").then((d) => { dictionaryCache["en"] = d; });

const useTranslator = () => {
  // useTranslation provides reactivity: when i18n.changeLanguage() is called
  // (e.g. from locale-switcher), this hook re-renders so the new cookie value is picked up.
  const { i18n } = useTranslation();
  const locale = (parseCookies()[LOCALE] as Locale) || (i18n.language as Locale) || "he";

  const [dictionary, setDictionary] = useState<DictType>(
    () => dictionaryCache[locale] || {},
  );

  useEffect(() => {
    // If cache already has this locale's dictionary, use it directly
    if (dictionaryCache[locale]) {
      setDictionary(dictionaryCache[locale]);
      return;
    }

    const fetchTranslations = async () => {
      const fetchedDictionary: DictType = await getDictionary(locale);
      dictionaryCache[locale] = fetchedDictionary;
      setDictionary(fetchedDictionary);
    };

    fetchTranslations();
  }, [locale]);

  const translate = useCallback(
    (text: string) => {
      return (text && dictionary[text.toLocaleLowerCase()]) || text;
    },
    [dictionary],
  );

  return { translate, locale };
};

export default useTranslator;
